import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { searchDocuments } from "../../utils/Api";
import type {
  InterpretedPlan,
  MatchType,
  NumericFilter,
  SearchResult,
} from "../../utils/Api";
import DocumentsSkeleton from "../../components/SkeletonLoading/DocumentsSkeleton";
import SearchBar from "../../components/SearchBar";
import { formatFilterLabel } from "./numericFilters";
import { intentLabel, reactionChipLabel } from "./interpretedPlan";

// Results route for this page (mirrors SearchBar's SEARCH_PATH). Filter
// changes re-navigate here so the URL stays the single source of truth.
const SEARCH_PATH = "/home/search";

/** Shared shape for the small status pills on this page. */
const PILL_BASE =
  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide";

/** Subtle pill flagging that document search is still in beta. */
const BetaBadge: React.FC = () => (
  <span className={`${PILL_BASE} bg-amber-100 text-amber-700`}>Beta</span>
);

// Only match types that need an explicit badge get one. Keyword/hybrid hits are
// typically implicit via a highlighted <mark> snippet. SMILES hits always show
// the dedicated "Structure match" block paired with the matched SMILES string;
// reaction hits additionally show the matched {smiles, role} constraints below.
const MATCH_TYPE_BADGE: Partial<
  Record<MatchType, { label: string; className: string }>
> = {
  semantic: { label: "Related match", className: "bg-emerald-100 text-emerald-700" },
  reaction: { label: "Reaction match", className: "bg-purple-100 text-purple-700" },
};

/**
 * Explains a hit whose relevance isn't obvious from the snippet — a semantic
 * (meaning) match, or a reaction-role match — so it doesn't look broken. Renders
 * nothing for keyword/hybrid/smiles matches.
 */
const MatchTypeBadge: React.FC<{ matchType?: MatchType }> = ({ matchType }) => {
  const meta = matchType ? MATCH_TYPE_BADGE[matchType] : undefined;
  if (!meta) return null;
  return <span className={`${PILL_BASE} ${meta.className}`}>{meta.label}</span>;
};

const PAGE_SIZE = 20;

/**
 * Render a `ts_headline` snippet safely. The backend delivers matches wrapped in
 * `<b>…</b>` tags; we split on those tags and render the highlighted segments as
 * <mark> React nodes rather than injecting raw HTML — no dangerouslySetInnerHTML,
 * so there is no XSS risk from the snippet content.
 */
function renderSnippet(snippet: string): React.ReactNode {
  const parts = snippet.split(/<b>|<\/b>/);
  // Even indices are plain text, odd indices are the highlighted matches.
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="bg-amber-100 text-gray-900 rounded-sm px-0.5">
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}

// A few worked examples that make the natural-language capability discoverable
// (Search V4) — full questions spanning the semantic, numeric and reaction-role
// lanes, so a user learns they can just ask.
const EXAMPLE_QUERIES = [
  "Show me all the documents related to aspirin",
  "Polymers with Tg between 140 and 160 and good wear resistance",
  "Papers with a reaction scheme where CCCO is a reactant",
];

/**
 * Clickable example queries surfaced near the search box (landing + zero-results
 * states). Selecting one runs that query, teaching the numeric grammar by doing.
 */
const ExampleQueries: React.FC<{ onSelect: (q: string) => void }> = ({
  onSelect,
}) => (
  <div className="flex flex-wrap items-center justify-center gap-2">
    <span className="text-sm text-gray-400">Try:</span>
    {EXAMPLE_QUERIES.map((example) => (
      <button
        key={example}
        type="button"
        onClick={() => onSelect(example)}
        className={`${PILL_BASE} bg-gray-100 text-gray-600 normal-case hover:bg-gray-200 transition-colors`}
      >
        {example}
      </button>
    ))}
  </div>
);

// A numeric echo is removable only when the backend gave it a span to excise.
// Heuristic-parsed constraints carry `[span_start, span_end)` offsets into the
// query; planner-derived constraints (Search V4) have null spans — there is no
// reliable source span in an LLM paraphrase — so those chips render read-only
// rather than with a dead remove button.
const hasSpan = (echo: NumericFilter): boolean =>
  typeof echo.span_start === "number" && typeof echo.span_end === "number";

/**
 * The applied numeric constraints as chips. Chips are driven entirely by the
 * backend echo (`data.numeric_filters`) — the typed query is the single source
 * of truth. A span-removable chip gets an `X` that excises its matched span
 * (`span_start`/`span_end`) from the query; "Clear all" excises every removable
 * one. Chips without a span (planner-origin) are read-only.
 */
const ActiveFilters: React.FC<{
  applied: NumericFilter[];
  onRemove: (echo: NumericFilter) => void;
  onClearAll: () => void;
}> = ({ applied, onRemove, onClearAll }) => {
  if (applied.length === 0) return null;
  const anyRemovable = applied.some(hasSpan);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {applied.map((echo, index) => {
        const label = formatFilterLabel(echo);
        return (
          <span
            key={`${echo.property_key}-${index}`}
            className={`${PILL_BASE} bg-sky-100 text-sky-800 normal-case`}
          >
            {label}
            {hasSpan(echo) && (
              <button
                type="button"
                onClick={() => onRemove(echo)}
                aria-label={`Remove filter ${label}`}
                className="ml-1 -mr-1 rounded-full p-0.5 hover:bg-sky-200 transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </span>
        );
      })}
      {anyRemovable && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
};

/**
 * The NL planner's interpretation of the prompt (Search V4), as a
 * read-only "Understood as" panel — the natural-language analogue of the numeric
 * chips. Rendered only on the confident LLM-planner path (when the backend
 * echoes `interpreted_plan`); on the deterministic heuristic fallback the plan
 * is absent and this renders nothing, so the page looks exactly as it does today.
 * Chips are informational (no remove button): the user adjusts by editing the
 * question — the planner rebuilds the whole plan from the prompt, so there is no
 * per-lane toggle to expose.
 */
const InterpretedPlanPanel: React.FC<{ plan: InterpretedPlan }> = ({ plan }) => {
  // The plan is Pydantic-validated server-side, but read every field
  // defensively (as the rest of this page does with the search response) so one
  // malformed/absent field can't white-screen the whole results view.
  const freeText = (plan.free_text ?? "").trim();
  const exactTokens = plan.exact_tokens ?? [];
  const smiles = plan.smiles ?? [];
  const reactions = plan.reaction_constraints ?? [];

  // The lane chips are the substance of the panel; the intent chip alone (e.g. a
  // numeric-only query, whose understanding rides in `numeric_filters`) says
  // nothing, so suppress the panel entirely when no lane was understood.
  const laneChips: React.ReactNode[] = [];

  if (freeText) {
    laneChips.push(
      <span
        key="free-text"
        className={`${PILL_BASE} bg-gray-100 text-gray-700 normal-case`}
      >
        {freeText}
      </span>,
    );
  }

  exactTokens.forEach((token, i) =>
    laneChips.push(
      <span
        key={`token-${i}`}
        className={`${PILL_BASE} bg-gray-100 text-gray-700 normal-case`}
      >
        {`"${token}"`}
      </span>,
    ),
  );

  smiles.forEach((s, i) =>
    laneChips.push(
      <span
        key={`smiles-${i}`}
        className={`${PILL_BASE} bg-indigo-100 text-indigo-700 normal-case font-mono break-all`}
      >
        {s}
      </span>,
    ),
  );

  reactions.forEach((reaction, i) =>
    laneChips.push(
      <span
        key={`reaction-${i}`}
        className={`${PILL_BASE} bg-purple-100 text-purple-700 normal-case font-mono break-all`}
      >
        {reactionChipLabel(reaction)}
      </span>,
    ),
  );

  if (laneChips.length === 0) return null;

  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/50 px-3 py-2.5">
      <p className="text-xs font-medium text-violet-800 mb-2">Understood as</p>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`${PILL_BASE} bg-violet-100 text-violet-800 normal-case`}
        >
          {intentLabel(plan.intent)}
        </span>
        {laneChips}
      </div>
    </div>
  );
};

const Search: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The typed query is the single source of truth (#536): the backend parses
  // numeric constraints out of it server-side (#522), so `q` is all we send.
  const query = (searchParams.get("q") ?? "").trim();
  const hasActiveSearch = query.length > 0;

  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  // The constraints the backend actually applied, echoed back for authoritative
  // chip labels (canonical display/unit/comparator).
  const [appliedFilters, setAppliedFilters] = useState<NumericFilter[]>([]);
  // The NL planner's interpretation of the prompt, echoed only on the confident
  // LLM-planner path. Null on the heuristic fallback → no plan panel.
  const [interpretedPlan, setInterpretedPlan] = useState<InterpretedPlan | null>(
    null,
  );

  // Tracks the query of the most recent request so a slow response for an
  // outdated query can't overwrite results for the current one (stale-response race).
  const latestQueryRef = useRef(query);

  const fetchResults = useCallback(
    async (offset: number, append: boolean) => {
      if (!query) {
        setResults([]);
        setTotal(0);
        setError(null);
        setAppliedFilters([]);
        setInterpretedPlan(null);
        // Clear loading here: a still-in-flight request for a previous query
        // won't run its finally reset once this query becomes the latest.
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      try {
        if (offset === 0) {
          setLoading(true);
          // A first-page fetch means the query changed (pagination appends), so
          // drop the previous query's header echoes now rather than after the
          // response — otherwise a slow planner response would leave the old
          // "Understood as" panel and numeric chips showing above the new
          // query's loading skeleton.
          setInterpretedPlan(null);
          setAppliedFilters([]);
        } else {
          setLoadingMore(true);
        }

        const { data } = await searchDocuments(query, {
          limit: PAGE_SIZE,
          offset,
        });

        // Bail out if the query moved on while this request was in flight.
        if (latestQueryRef.current !== query) return;

        const newResults: SearchResult[] = data.results ?? [];
        if (append) {
          setResults((prev) => [...prev, ...newResults]);
        } else {
          setResults(newResults);
          // The interpreted plan describes the whole query, so only the first
          // page carries it — don't let a "Show more" fetch clobber it.
          setInterpretedPlan(data.interpreted_plan ?? null);
        }
        setTotal(data.total ?? 0);
        setAppliedFilters(data.numeric_filters ?? []);
        setError(null);
      } catch (err: any) {
        if (latestQueryRef.current !== query) return; // ignore stale failure
        setError(err.message ?? "Error searching documents");
        if (!append) {
          setResults([]);
          setInterpretedPlan(null);
        }
      } finally {
        // Only the current query's request should clear the loading state.
        if (latestQueryRef.current === query) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [query],
  );

  useEffect(() => {
    latestQueryRef.current = query;
    fetchResults(0, false);
  }, [fetchResults, query]);

  const remainingCount = total - results.length;

  const handleShowMore = () => {
    fetchResults(results.length, true);
  };

  // Run an arbitrary query (e.g. an example chip) by navigating to the results
  // route; the effect above re-runs the search off the new `q`.
  const runQuery = (nextQuery: string) => {
    const q = nextQuery.trim();
    navigate(q ? `${SEARCH_PATH}?q=${encodeURIComponent(q)}` : SEARCH_PATH);
  };

  // Connector words the backend parser drops between constraints
  // (api/api/search/constraints.py `_CONNECTORS`). We tidy them from the residual
  // after excising a span so the visible query keeps no stray "and".
  const CONNECTORS = new Set(["and", "with", "having", "plus", "&"]);

  // Excise the given `[start, end)` spans (offsets into the current query) and
  // tidy the residual: collapse whitespace, fold consecutive connectors down to
  // one (excising a span between two connectors would otherwise leave "and and"),
  // then drop connectors stranded at either end. Spans are removed
  // high-offset-first so earlier offsets stay valid.
  const exciseSpans = (spans: Array<[number, number]>): string => {
    let next = query;
    for (const [start, end] of [...spans].sort((a, b) => b[0] - a[0])) {
      next = next.slice(0, start) + next.slice(end);
    }
    const tokens = next.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
    const isConnector = (t: string | undefined) =>
      t !== undefined && CONNECTORS.has(t.toLowerCase());
    const kept: string[] = [];
    for (const t of tokens) {
      // Skip a connector immediately following another connector.
      if (isConnector(t) && isConnector(kept[kept.length - 1])) continue;
      kept.push(t);
    }
    while (isConnector(kept[0])) kept.shift();
    while (isConnector(kept[kept.length - 1])) kept.pop();
    return kept.join(" ");
  };

  // The consumed span of an echoed constraint, or null when the backend didn't
  // provide offsets (a planner-origin constraint, or an older API without spans).
  // Shares the `hasSpan` predicate so removability is decided in one place.
  const spanOf = (echo: NumericFilter): [number, number] | null =>
    hasSpan(echo) ? [echo.span_start as number, echo.span_end as number] : null;

  // Remove an applied numeric constraint by excising its exact span from the
  // query — the typed query is the single source of truth, so there is no
  // separate filter model to mutate (#536).
  const handleRemoveConstraint = (echo: NumericFilter) => {
    const span = spanOf(echo);
    if (!span) return;
    runQuery(exciseSpans([span]));
  };

  const handleClearConstraints = () => {
    const spans = appliedFilters
      .map(spanOf)
      .filter((s): s is [number, number] => s !== null);
    runQuery(exciseSpans(spans));
  };

  // Landing state: no active query yet. Show a large, centred, Google-style
  // input with the brand feel and the beta notice.
  if (!hasActiveSearch) {
    return (
      <div className="bg-white h-full flex flex-col items-center justify-center overflow-y-auto px-4 py-12">
        <div className="w-full max-w-2xl flex flex-col items-center text-center">
          <img
            src="/DataRevival_FC_RGB.png"
            alt="Data Revival"
            className="w-56 h-auto mb-6"
          />
          <div className="mb-6 flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
              Search documents
            </h1>
            <BetaBadge />
          </div>

          <SearchBar variant="hero" autoFocus />

          <div className="mt-5">
            <ExampleQueries onSelect={runQuery} />
          </div>
        </div>
      </div>
    );
  }

  // Results state: active query. The refine input lives at the top of the page.
  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-8 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="mb-6">
          <SearchBar variant="compact" />
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">
              Search results
            </h3>
            <BetaBadge />
          </div>
          <p className="text-base text-gray-500 mt-0.5">
            {`Showing results for "${query}"`}
          </p>
        </div>

        {interpretedPlan && (
          <div className="mb-4">
            <InterpretedPlanPanel plan={interpretedPlan} />
          </div>
        )}

        {appliedFilters.length > 0 && (
          <div className="mb-6">
            <ActiveFilters
              applied={appliedFilters}
              onRemove={handleRemoveConstraint}
              onClearAll={handleClearConstraints}
            />
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10">
        {loading ? (
          <DocumentsSkeleton />
        ) : results.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">
            <p className="font-medium text-gray-600 mb-1">No results</p>
            <p className="mb-5">{`No results for “${query}”`}</p>
            <ExampleQueries onSelect={runQuery} />
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-gray-100">
            {results.map((result) => (
              <li key={result.document_id} className="py-5">
                <button
                  type="button"
                  className="text-lg font-medium text-[#1d365d] hover:text-[#2d4a75] hover:underline cursor-pointer bg-transparent p-0 border-0 text-left"
                  onClick={() => navigate(`/view/${result.document_id}`)}
                >
                  {result.name || "Untitled Document"}
                </button>

                {result.match_type && MATCH_TYPE_BADGE[result.match_type] && (
                  <p className="mt-1">
                    <MatchTypeBadge matchType={result.match_type} />
                  </p>
                )}

                {result.matched_smiles && (
                  <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                    <span className={`${PILL_BASE} bg-indigo-100 text-indigo-700`}>
                      Structure match
                    </span>
                    <span className="font-mono text-gray-600 break-all">
                      {result.matched_smiles}
                    </span>
                  </p>
                )}

                {result.matched_reactions &&
                  result.matched_reactions.length > 0 && (
                    <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                      {/* Label the constraints only when the top-of-row
                          "Reaction match" badge isn't already shown (a hybrid
                          hit carries reactions but no reaction badge). */}
                      {result.match_type !== "reaction" && (
                        <span
                          className={`${PILL_BASE} bg-purple-100 text-purple-700`}
                        >
                          Reaction
                        </span>
                      )}
                      {result.matched_reactions.map((reaction, i) => (
                        <span
                          key={`${reaction?.smiles}-${reaction?.role}-${i}`}
                          className="font-mono text-gray-600 break-all"
                        >
                          {reactionChipLabel(reaction)}
                        </span>
                      ))}
                    </p>
                  )}

                {result.matched_pages.length > 0 && (
                  <p className="mt-1 text-xs text-gray-400">
                    {`Pages ${result.matched_pages.join(", ")}`}
                  </p>
                )}

                {result.snippet ? (
                  <p className="mt-1.5 text-sm text-gray-600">
                    {renderSnippet(result.snippet)}
                  </p>
                ) : (
                  // Name-only hit: no page content matched, so give a muted
                  // explanatory line rather than a bare title. A structure or
                  // reaction hit matched content, so it isn't name-only.
                  result.matched_pages.length === 0 &&
                  !result.matched_smiles &&
                  !result.matched_reactions?.length && (
                    <p className="mt-1.5 text-sm italic text-gray-400">
                      Matched on document name
                    </p>
                  )
                )}
              </li>
            ))}
          </ul>
        )}

        {/* pagination controls */}
        {!loading && results.length > 0 && (
          <div className="mt-6 pb-12 flex flex-col items-center gap-3">
            {remainingCount > 0 && (
              <button
                onClick={handleShowMore}
                disabled={loadingMore}
                className="px-4 py-2 text-sm font-medium text-[#1d365d] border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {loadingMore ? "Loading..." : "Show more"}
              </button>
            )}
            <p className="text-sm text-gray-400">
              {results.length} of {total} results
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
